"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState, useRef } from "react";
import CartItem from "@/components/ui/CartItem";
import { getStoredSession } from "@/lib/auth";
import { checkDiyStatus } from "@/lib/diy";
import { getActivePaymentSessionSnapshot } from "@/lib/activePaymentSession";
import { clearDiyEditorContext } from "@/context/diyEditorContext";

function formatIDR(amount) {
    return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
    }).format(amount);
}

export default function CartDrawer() {
    const {
        items, open, setOpen, subtotal, clearCart,
        selectedAddons, toggleAddon,
        masaAktif, setMasaAktif,
        customDomainValue, setCustomDomainValue,
        totalItems, orderConfig, isConfigLoading
    } = useCart();
    const { requireAuth, showToast, canCheckout, openProfileSettings, user } = useAuth();
    const router = useRouter();
    
    const [diyStatuses, setDiyStatuses] = useState({});
    const [isCheckingActiveSession, setIsCheckingActiveSession] = useState(false);
    const checkedSlugs = useRef(new Set());

    const ADDON_ITEMS = orderConfig?.addons || [];
    const ACTIVE_PERIOD_OPTIONS = orderConfig?.activePeriodOptions || [];

    const addonsTotal =
        ADDON_ITEMS.reduce(
            (sum, { key, discountedPrice }) => sum + (selectedAddons[key] ? discountedPrice : 0),
            0
        ) + (ACTIVE_PERIOD_OPTIONS.find((o) => o.value === masaAktif)?.price ?? 0);

    const isDiyReady = !user || !items.some((item) => item.type === "invitation" && item.slug && diyStatuses[item.slug] === undefined);
    const isCartReady = !isConfigLoading && isDiyReady;

    useEffect(() => {
        if (open) {
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.body.style.overflow = "hidden";
            document.body.style.paddingRight = `${scrollbarWidth}px`;
        } else {
            document.body.style.overflow = "";
            document.body.style.paddingRight = "";
        }
        return () => {
            document.body.style.overflow = "";
            document.body.style.paddingRight = "";
        };
    }, [open]);

    useEffect(() => {
        if (!open) {
            checkedSlugs.current.clear();
            return;
        }
        
        if (!user) return;
        
        const session = getStoredSession();
        if (!session?.token) return;

        const checkStatuses = async () => {
            for (const item of items) {
                if (item.type === "invitation" && item.slug && !checkedSlugs.current.has(item.slug)) {
                    checkedSlugs.current.add(item.slug);
                    try {
                        const data = await checkDiyStatus(item.slug);
                        setDiyStatuses(prev => ({ ...prev, [item.slug]: data?.data?.exists === true }));
                    } catch (err) {
                        console.error("Failed to check DIY status:", err);
                        setDiyStatuses(prev => ({ ...prev, [item.slug]: false }));
                    }
                }
            }
        };

        checkStatuses();
    }, [items, user, open]);

    useEffect(() => {
        function onKey(e) {
            if (e.key === "Escape") setOpen(false);
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [setOpen]);

    const handleToggleAddon = (key, label) => {
        const isSelecting = !selectedAddons[key];
        toggleAddon(key);
        showToast(isSelecting ? `${label} ditambahkan.` : `${label} dihapus.`);
    };

    const handleSelectMasaAktif = (value, label) => {
        if (masaAktif === value) {
            return;
        }
        setMasaAktif(value);
        showToast(`Masa aktif ${label} dipilih.`);
    };

    const handleCheckoutClick = async (event) => {
        event.preventDefault();

        if (!isCartReady) {
            return;
        }

        if (isCheckingActiveSession) {
            return;
        }

        if (!requireAuth({ redirectPath: "/checkout" })) {
            return;
        }

        if (!user) {
            showToast("Data akun sedang dimuat, coba lagi.");
            return;
        }

        if (!canCheckout) {
            showToast("Verifikasi email dan nomor WhatsApp dulu sebelum checkout.");
            openProfileSettings();
            return;
        }

        setIsCheckingActiveSession(true);
        try {
            const activeSession = await getActivePaymentSessionSnapshot();
            if (activeSession?.paymentSessionId) {
                setOpen(false);
                showToast("Masih ada pembayaran aktif. Lanjutkan atau batalkan dulu dari Orders.");
                router.push("/orders");
                return;
            }
        } catch {
            // Allow fallback to checkout if active-session endpoint is temporarily unavailable.
        } finally {
            setIsCheckingActiveSession(false);
        }

        setOpen(false);
        router.push("/checkout");
    };

    return (
        <>
            {open && (
                <div
                    className="cart-backdrop"
                    onClick={() => setOpen(false)}
                    aria-hidden="true"
                />
            )}

            <div className={`cart-drawer${open ? " cart-drawer--open" : ""}`}>
                {/* Header */}
                <div className="cart-drawer__header">
                    <div className="flex items-center gap-2">
                        <CartBagIcon className="h-5 w-5 text-primary" />
                        <span className="font-semibold text-zinc-900">
                            Keranjang ({totalItems})
                        </span>
                    </div>
                    <button
                        onClick={() => setOpen(false)}
                        className="cart-drawer__close cursor-pointer"
                        aria-label="Tutup keranjang"
                    >
                        <CloseIcon />
                    </button>
                </div>

                {/* Body */}
                <div className="cart-drawer__body">
                    {items.length === 0 ? (
                        <div className="cart-drawer__empty">
                            <CartBagIcon className="h-12 w-12 text-primary-light" />
                            <p className="mt-3 text-sm text-zinc-500">
                                Keranjang kamu masih kosong.
                            </p>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-6">
                            {/* Items */}
                            <ul className="flex flex-col gap-4">
                                {items.map((item) => (
                                    <CartItem key={item.id} item={item} />
                                ))}
                            </ul>

                            {!isCartReady ? (
                                <div className="flex min-h-50 flex-col items-center justify-center text-primary">
                                    <svg className="h-8 w-8 animate-spin" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <p className="mt-4 text-xs font-medium text-zinc-500">Mempersiapkan opsi kustomisasi...</p>
                                </div>
                            ) : (
                                <>
                            {/* DO IT YOURSELF */}
                            <div className="flex flex-col gap-3">
                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">
                                        Customization
                                    </p>
                                    <p className="mt-1 text-sm font-semibold text-zinc-800">
                                        DO IT YOURSELF <span className="text-xs font-semibold text-orange-500">(BETA)</span> <span className="text-xs font-normal text-green-600">(Gratis berjangka waktu)</span>
                                    </p>
                                    <p className="mt-1 text-xs text-zinc-500">
                                        Opsional - Jika tidak di-customize, kami akan gunakan layout default template.
                                    </p>
                                </div>

                                <div className="flex flex-col gap-2">
                                    {items.map((item) => {
                                        if (item.type !== "invitation" || !item.slug) return null;
                                        const hasDiy = diyStatuses[item.slug] === true;
                                        return (
                                            <div key={item.id} className="flex items-center justify-between rounded-xl border border-zinc-200 bg-white p-3">
                                                <div className="flex items-center gap-2.5">
                                                    <div className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${hasDiy ? "bg-green-500" : "bg-zinc-300"}`}>
                                                        {hasDiy ? (
                                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" className="h-2.5 w-2.5">
                                                                <polyline points="20 6 9 17 4 12" />
                                                            </svg>
                                                        ) : (
                                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-2.5 w-2.5">
                                                                <circle cx="12" cy="12" r="10" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <span className="text-sm font-medium text-zinc-900">
                                                            {item.name}
                                                        </span>
                                                        <p className="text-xs text-zinc-500">
                                                            {hasDiy ? "Cloud DIY Ready" : "Belum di-customize"}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {hasDiy && (
                                                        <Link
                                                            href={`/templates/${item.slug}/preview`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex h-8 items-center justify-center rounded-full border border-primary-light px-3 text-xs font-semibold text-primary-dark transition hover:border-primary hover:text-primary"
                                                        >
                                                            Preview
                                                        </Link>
                                                    )}
                                                    <button
                                                        onClick={() => {
                                                            if (!requireAuth()) {
                                                                sessionStorage.setItem("pending_redirect", `/templates/${item.slug}/diy`);
                                                                return;
                                                            }
                                                            clearDiyEditorContext(item.slug);
                                                            window.location.href = `/templates/${item.slug}/diy`;
                                                        }}
                                                        className="cursor-pointer rounded-full border border-primary-light p-2 text-xs font-semibold text-primary-dark transition hover:border-primary hover:text-primary"
                                                        title={hasDiy ? "Edit Customization" : "Go to Editor"}
                                                        aria-label={hasDiy ? "Edit Customization" : "Go to Editor"}
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                            <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Add-Ons */}
                            <div className="flex flex-col gap-3">
                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">
                                        Add-Ons
                                    </p>
                                    <p className="mt-1 text-sm font-semibold text-zinc-800">
                                        Custom
                                    </p>
                                </div>

                                <div className="flex flex-col gap-2">
                                    {ADDON_ITEMS.filter((a) => a.key !== "diy").map(({ key, label, discountedPrice, price }) => (
                                        <div key={key} className="flex flex-col gap-2">
                                            <button
                                                onClick={() => handleToggleAddon(key, label)}
                                                className={`flex w-full items-center justify-between rounded-xl border p-3 text-left transition ${selectedAddons[key]
                                                    ? "border-primary bg-primary/5"
                                                    : "border-zinc-200 bg-white"
                                                    }`}
                                            >
                                                <div className="flex items-center gap-2.5">
                                                    <div
                                                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition ${selectedAddons[key]
                                                            ? "border-primary bg-primary"
                                                            : "border-zinc-300 bg-white"
                                                            }`}
                                                    >
                                                        {selectedAddons[key] && (
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="white"
                                                                strokeWidth="3.5"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                className="h-2.5 w-2.5"
                                                            >
                                                                <polyline points="20 6 9 17 4 12" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <span className="text-sm font-medium text-zinc-900">
                                                        {label}
                                                    </span>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-sm font-semibold text-primary-dark">
                                                        {discountedPrice === 0 ? "Gratis" : formatIDR(discountedPrice)}
                                                    </p>
                                                    {price && (
                                                        <p className="text-xs text-zinc-500 line-through">
                                                            Normal {formatIDR(price)}
                                                        </p>
                                                    )}
                                                </div>
                                            </button>

                                            {/* Custom Domain input */}
                                            {key === "customDomain" && selectedAddons.customDomain && (
                                                <div className="rounded-xl border border-primary/30 bg-primary/5 px-3 py-2.5">
                                                    <label className="mb-1.5 block text-xs font-medium text-zinc-500">
                                                        Masukkan request domain kamu
                                                    </label>
                                                    <div className="flex items-center gap-0 overflow-hidden rounded-lg border border-primary-light bg-white focus-within:border-primary transition">
                                                        <span className="border-r border-primary-light px-3 py-2 text-sm text-zinc-400 select-none bg-zinc-50">
                                                            www.
                                                        </span>
                                                        <input
                                                            id="customDomain"
                                                            name="customDomain"
                                                            autoComplete="url"
                                                            type="text"
                                                            placeholder="namadomainmu.com"
                                                            value={customDomainValue}
                                                            onChange={(e) => setCustomDomainValue(e.target.value)}
                                                            className="flex-1 bg-transparent px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none"
                                                        />
                                                    </div>
                                                    <p className="mt-2 text-[11px] text-zinc-500">Tim kami akan menghubungi kamu untuk mengkonfirmasi ketersediaan domain.</p>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Masa Aktif */}
                                <div className="flex flex-col gap-2">
                                    <p className="text-sm font-semibold text-zinc-800">
                                        Masa Aktif{" "}
                                        <span className="text-xs font-normal text-zinc-500">
                                            (Gratis 6 Bulan)
                                        </span>
                                    </p>
                                    {ACTIVE_PERIOD_OPTIONS.map(({ value, label, price }) => (
                                        <button
                                            key={value}
                                            onClick={() => handleSelectMasaAktif(value, label)}
                                            className={`flex w-full items-center justify-between rounded-xl border p-3 text-left transition ${masaAktif === value
                                                ? "border-primary bg-primary/5"
                                                : "border-zinc-200 bg-white"
                                                }`}
                                        >
                                            <div className="flex items-center gap-2.5">
                                                <div
                                                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition ${masaAktif === value
                                                        ? "border-primary"
                                                        : "border-zinc-300"
                                                        }`}
                                                >
                                                    {masaAktif === value && (
                                                        <div className="h-2 w-2 rounded-full bg-primary" />
                                                    )}
                                                </div>
                                                <span className="text-sm font-medium text-zinc-900">
                                                    {label}
                                                </span>
                                            </div>
                                            <span className="text-sm font-semibold text-primary-dark">
                                                {price === 0 ? "Gratis" : formatIDR(price)}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                {items.length > 0 && (
                    <div className="cart-drawer__footer">
                        <div className="flex flex-col gap-1.5 text-sm">
                            <div className="flex items-center justify-between">
                                <span className="text-zinc-600">Template</span>
                                <span className="font-semibold text-zinc-900">
                                    {formatIDR(subtotal)}
                                </span>
                            </div>
                            {addonsTotal > 0 && (
                                <div className="flex items-center justify-between">
                                    <span className="text-zinc-600">Add-Ons</span>
                                    <span className="font-semibold text-zinc-900">
                                        {formatIDR(addonsTotal)}
                                    </span>
                                </div>
                            )}
                            <div className="flex items-center justify-between border-t border-zinc-100 pt-2 text-base">
                                <span className="font-semibold text-zinc-900">Total</span>
                                <span className="font-bold text-primary-dark">
                                    {formatIDR(subtotal + addonsTotal)}
                                </span>
                            </div>
                        </div>
                        <p className="mt-1 text-xs text-zinc-500">
                            Biaya pelayanan dan pajak dihitung saat checkout.
                        </p>
                        <Link
                            href={isCartReady ? "/checkout" : "#"}
                            onClick={handleCheckoutClick}
                            className={`cart-drawer__checkout block text-center ${!isCartReady || isCheckingActiveSession ? "opacity-50 cursor-not-allowed" : ""}`}
                            aria-disabled={!isCartReady || isCheckingActiveSession}
                            aria-busy={isCheckingActiveSession}
                        >
                            {"Lanjut ke Checkout"}
                        </Link>
                        <button
                            onClick={clearCart}
                            className="mt-2 w-full cursor-pointer text-center text-xs text-zinc-500 underline transition hover:text-red-500"
                        >
                            Kosongkan keranjang
                        </button>
                    </div>
                )}
            </div>
        </>
    );
}

function CartBagIcon({ className }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
        >
            <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <path d="M16 10a4 4 0 0 1-8 0" />
        </svg>
    );
}

function CloseIcon() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-5 w-5"
        >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    );
}
